import { useSEO } from '@/hooks/useSEO';
import { useState } from "react";
import PageNavbar from "@/components/feature/PageNavbar";
import Footer from "@/pages/home/components/Footer";
import { submitForm } from "@/lib/submitForm";

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://isman.co.ke';

const RAILING_SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Stainless Steel Railing Systems Kenya",
    "url": SITE_URL + "/stainless-railing",
    "description": "Architecturally designed SS304 and SS316 balustrades, handrails and guardrails fabricated and installed across Kenya. Mirror, satin and powder-coated finishes by ISMAN Engineering Nairobi.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "ISMAN Engineering Company",
      "url": SITE_URL,
      "telephone": "+254724114555"
    },
    "areaServed": { "@type": "Country", "name": "Kenya" },
    "serviceType": "Stainless Steel Railing & Balustrade"
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
      { "@type": "ListItem", "position": 2, "name": "Stainless Railing", "item": SITE_URL + "/stainless-railing" }
    ]
  }
];

const offerings = [
  { icon: "ri-building-line", image: "/images/railing/p-balustrade.webp", title: "Balustrade Systems", desc: "Architecturally designed glass-infill or bar-infill balustrades in SS304/SS316 — indoor and outdoor, residential and commercial." },
  { icon: "ri-route-line", image: "/images/railing/p-staircase.webp", title: "Staircase Handrails", desc: "Custom-profiled continuous handrails for straight, curved and spiral staircases — mirror, satin or powder-coated finishes." },
  { icon: "ri-community-line", image: "/images/railing/p-pool.webp", title: "Pool & Deck Railings", desc: "Marine-grade SS316 pool fence and deck railing systems — fully welded or mechanical fittings, compliant with Building Code." },
  { icon: "ri-government-line", image: "/images/railing/p-mezzanine.webp", title: "Mezzanine Edge Protection", desc: "Industrial mezzanine and platform guardrail systems — knee rail, mid rail and top rail to factory safety standards." },
  { icon: "ri-store-2-line", image: "/images/railing/p-shopfront.webp", title: "Shopfront Barriers", desc: "Retail shopfront dividers, queue barriers and display stand supports fabricated in satin SS304." },
  { icon: "ri-tools-line", image: "/images/railing/p-metalwork.webp", title: "Custom Metalwork", desc: "Bespoke decorative and functional metalwork — gates, canopies, pergola frames and architectural feature elements." },
];

const finishes = [
  { name: "Mirror Polished", image: "/images/railing/f-mirror.webp", desc: "#8 / BA finish — highly reflective, used in luxury interiors and hospitality.", color: "from-gray-200 to-gray-400" },
  { name: "Satin Brushed", image: "/images/railing/f-satin.webp", desc: "No.4 finish — linear grain, most popular for commercial and food-service.", color: "from-gray-300 to-gray-500" },
  { name: "Powder Coated", image: "/images/railing/f-powder.webp", desc: "Any RAL colour — textured or gloss, used for exterior and architectural elements.", color: "from-teal-400 to-teal-600" },
  { name: "Electro-Polished", image: "/images/railing/f-electro.webp", desc: "Ultra-clean surface for pharmaceutical and food-grade environments.", color: "from-slate-200 to-slate-400" },
];

export default function StainlesRailingPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", type: "Balustrade System", location: "", details: "" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const data = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => data.append(k, v));
    try {
      await submitForm("stainless-railing", data);
    } catch (_e) { /* ignore */ }
    setSubmitting(false);
    setSubmitted(true);
  };

  useSEO({
    title: 'Stainless Steel Railing Systems Kenya | SS304 Balustrade Handrails | ISMAN Nairobi',
    description: 'Architecturally designed stainless steel SS304 and SS316 balustrades, handrails and guardrails fabricated in Nairobi and installed across Kenya. ISMAN Engineering.',
    keywords: 'stainless steel railing Kenya, balustrade Nairobi, SS304 handrail Kenya, stainless balustrade Kenya, glass railing Nairobi, pool railing Kenya',
    canonical: SITE_URL + '/stainless-railing',
    schema: RAILING_SCHEMA,
  });

  return (
    <main className="min-h-screen bg-white">
      <PageNavbar />
      <section className="relative h-[480px] flex items-end overflow-hidden">
        <img src="/images/railing/pool-stairs.webp" alt="Stainless steel and glass railing by ISMAN" className="absolute inset-0 w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6 pb-14 w-full">
          <span className="inline-block bg-slate-600 text-white text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-md mb-4">ISMAN Engineering</span>
          <h1 className="text-white text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Stainless Steel Railing Systems</h1>
          <p className="text-white/75 text-base max-w-2xl">Architecturally designed SS304 and SS316 balustrades, handrails and guardrails — fabricated in Nairobi and installed across Kenya.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-slate-600 text-xs font-bold uppercase tracking-widest">Products &amp; Services</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>Railing Solutions</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {offerings.map((o) => (
              <div key={o.title} className="bg-white rounded-xl border border-gray-100 hover:border-slate-300 transition-all group overflow-hidden">
                <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                  <img src={o.image} alt={o.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                <div className="w-11 h-11 flex items-center justify-center bg-slate-50 rounded-lg mb-4 group-hover:bg-slate-700 transition-colors">
                  <i className={`${o.icon} text-slate-600 group-hover:text-white text-xl transition-colors`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{o.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{o.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Finishes */}
      <section className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Available Finishes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {finishes.map((f) => (
              <div key={f.name} className="rounded-xl overflow-hidden border border-gray-100">
                <div className="relative h-40 overflow-hidden bg-gray-100">
                  <img src={f.image} alt={`${f.name} stainless steel finish`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-sm mb-1">{f.name}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="py-16 bg-gray-50 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Project Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { src: "terrace", label: "Glass Balustrade — Terrace" },
              { src: "staircase", label: "Staircase Handrail" },
              { src: "pool", label: "Pool Railing" },
              { src: "mezzanine", label: "Mezzanine Guardrail" },
              { src: "shopfront", label: "Shopfront & Queue Barriers" },
              { src: "entrance-pergola", label: "Gates, Canopy & Pergola" },
            ].map((g) => (
              <a key={g.src} href={`/images/railing/${g.src}.webp`} target="_blank" rel="noopener noreferrer" className="relative rounded-lg overflow-hidden group h-56 block">
                <img src={`/images/railing/${g.src}.webp`} alt={g.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-4 pt-10">
                  <span className="text-white text-sm font-semibold">{g.label}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section id="inquiry" className="py-16 px-4 md:px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Request a Railing Quote</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Tell us your railing requirements and we&apos;ll respond within 1 business day.</p>
          {submitted ? (
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-8 text-center">
              <i className="ri-check-double-line text-4xl text-teal-600 mb-3 block"></i>
              <h3 className="font-bold text-gray-900 mb-1">Enquiry Received!</h3>
              <p className="text-gray-500 text-sm">Our fabrication team will be in touch shortly.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-isman-form>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Full Name *</label>
                  <input required name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400" placeholder="David Ochieng" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Email *</label>
                  <input required type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400" placeholder="david@example.com" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Phone *</label>
                  <input required name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400" placeholder="0722 000 000" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Railing Type *</label>
                  <select required name="type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400 bg-white cursor-pointer">
                    {["Balustrade System", "Staircase Handrail", "Pool & Deck Railing", "Mezzanine Guardrail", "Custom Metalwork"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-700 block mb-1">Location / Project Site</label>
                  <input name="location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400" placeholder="e.g. Westlands, Nairobi" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Project Details</label>
                <textarea name="details" value={form.details} onChange={(e) => { if (e.target.value.length <= 500) setForm({ ...form, details: e.target.value }); }} rows={4} maxLength={500} className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-slate-400 resize-none" placeholder="Linear metres required, floor type, finish preference..."></textarea>
                <p className="text-xs text-gray-400 text-right mt-1">{form.details.length}/500</p>
              </div>
              <button type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-slate-700 to-gray-700 hover:from-slate-600 hover:to-gray-600 text-white font-bold py-3 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2">
                {submitting ? <><i className="ri-loader-4-line animate-spin"></i> Sending...</> : <><i className="ri-send-plane-line"></i> Send Enquiry</>}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
    </main>
  );
}
